/*
 OK FBI — Streamer Cue Cards library
 Curated Arabic host lines organized by phase. Written to sound natural for a Saudi/Gulf
 livestream presenter: professional, suspenseful, easy to say aloud. Avoid overly formal
 Arabic and theatrical cheese. Never spoils the case.
*/

export const PHASES = [
  { key: "opening",       label: "افتتاح القضية" },
  { key: "crime_scene",   label: "مسرح الجريمة" },
  { key: "evidence",      label: "كشف الأدلة" },
  { key: "cctv",          label: "كاميرات المراقبة" },
  { key: "interrogation", label: "الاستجواب" },
  { key: "fingerprint",   label: "فحص البصمات" },
  { key: "dna",           label: "تحليل DNA" },
  { key: "voting",        label: "التصويت المباشر" },
  { key: "accusation",    label: "الاتهام النهائي" },
  { key: "solved",        label: "حُلّت القضية" },
  { key: "failed",        label: "لم تُحل القضية" },
  { key: "intermission",  label: "استراحة التحقيق" },
];

export const PHASE_LABELS = Object.fromEntries(PHASES.map(p => [p.key, p.label]));

// Curated defaults — 4 lines per phase. Natural spoken tone.
export const DEFAULT_CUES = {
  opening: [
    "وصلنا الآن إلى مسرح الجريمة… ومن هذه اللحظة، كل تفصيل قد يغيّر مسار التحقيق.",
    "قضية جديدة، وجوه جديدة، ومحقق واحد بيقودكم فيها من أول خيط إلى آخر إجابة.",
    "خذوا نفس عميق… هذه القضية ما راح تكون سهلة، وأنا معكم خطوة بخطوة.",
    "قبل ما نبدأ… شاركوني في الشات مين أول واحد شكيتوا فيه.",
  ],
  crime_scene: [
    "هذا هو المكان اللي صار فيه كل شيء… ركزوا فيه، لأن التفاصيل مو دايم واضحة من أول نظرة.",
    "لاحظوا الترتيب حولنا، لاحظوا اللي مكانه ضروري، واللي يبدو أنه انزاح.",
    "بمجرد ما تدخلون مسرح جريمة… لازم يتغيّر أسلوب التفكير. لا نستنتج، بس نلاحظ.",
    "خلونا نمشي في المكان بهدوء… ومنكم اللي يلقط أول تفصيل غريب يكتبه في الشات.",
  ],
  evidence: [
    "لدينا دليل جديد… لكن لا تتسرعوا بالحكم. ركّزوا في التفاصيل قبل تربطونه بأي شخص.",
    "هذا الدليل ممكن يقلب القضية… أو ممكن يكون مجرد صدفة. الفرق بيتضح بعد الاستجواب.",
    "دليل زي هذا في العالم الحقيقي يوصل جرايم لسنوات. عندنا الآن، هو نقطة تحول.",
    "لاحظوا وين لُقي الدليل، وقت اكتشافه، ومَن كان قريب من المكان. الأدلة ما تكذب.",
  ],
  cctv: [
    "الآن نرجع إلى كاميرات المراقبة. راقبوا الوقت والحركة جيداً.",
    "الكاميرات ما تكذب، لكن تحتاج عين تعرف وين تركّز.",
    "لاحظوا مين داخل، مين طالع، ومين وقف أكثر من اللازم.",
    "أحياناً الجريمة كلها في ثانيتين من الفيديو… تبونها ولا لأ؟",
  ],
  interrogation: [
    "نبدأ الاستجواب… وأريدكم تركزون ليس فقط على الإجابة، بل على التناقضات.",
    "لاحظوا نبرة الصوت، تردده، ولحظات السكوت الطويلة… كلها تتكلم.",
    "السؤال الحقيقي مو دايم اللي أنا أسأله… أحياناً هو اللي المتهم يتجنّبه.",
    "اكتبوا في الشات: مين تحسون إجابته كانت أقوى، ومين كان يحاول يجمّل الحقيقة.",
  ],
  fingerprint: [
    "وصل تقرير البصمات… خلونا نشوف هل عندنا تطابق.",
    "البصمة إما تخلص القضية… وإما تفتح باب أسئلة أكبر.",
    "قبل ما أقرأ النتيجة، أعطوني توقعاتكم: بصمة مين نتوقع نشوف؟",
    "التحليل جاهز… وهذا اللي كنا ننتظره من زمان.",
  ],
  dna: [
    "نتيجة المختبر جاهزة… وهذه النتيجة قد تغيّر القضية بالكامل.",
    "DNA مو مثل البصمات… هذا دليل ما يعطينا مجال للاحتمالات.",
    "لحظة صمت من فضلكم… نحن على وشك رؤية اسم على الشاشة.",
    "الحمض النووي حسم قضايا كبيرة… هل يحسم هذه اليوم؟",
  ],
  voting: [
    "وصلنا للحظة القرار. من تتوقعون إنه المسؤول؟",
    "التصويت فُتح. لا تعتمدوا على الحدس فقط، راجعوا اللي شفناه.",
    "كل صوت الآن يحسب. صوّتوا بضمير، مو بضغط اللحظة.",
    "قبل ما نغلق التصويت… خذوا نظرة أخيرة على كل الأدلة.",
  ],
  accusation: [
    "القرار الآن نهائي… بعد كل ما شاهدناه، من هو المتهم؟",
    "لحظة الاتهام هي أصعب لحظة لأي محقق. تحمّلوا مسؤولية اختياركم.",
    "لا يوجد رجوع بعد هذه الخطوة. جاهزين؟",
    "نحن على وشك أن نعلن اسم واحد… ونغيّر حياته إلى الأبد.",
  ],
  solved: [
    "القضية أُغلقت. أحسنتم اللعب، وأحسنتم القراءة.",
    "هذا اللي وعدتكم فيه من البداية… عدالة، تفاصيل، وبعض المفاجآت.",
    "شكراً لكل واحد شارك، صوّت، أو حتى تابع بصمت. وصلنا بسلام.",
    "قضية جديدة قريباً… لكن مساء اليوم ينتمي لكم.",
  ],
  failed: [
    "لم نصل هذه المرة… لكن هذا جزء من التحقيق الحقيقي. ليس كل شيء يُحل.",
    "الأدلة كانت غامضة، والاستجواب ما كشف الجواب الكامل. نتعلم ونكمل.",
    "بعض القضايا تبقى مفتوحة… وهذي واحدة منها.",
    "شكراً لجهودكم. سنعود بقضية أصعب، ووعد بأن ما نخذلكم.",
  ],
  intermission: [
    "خذوا استراحة قصيرة… ورجعوا لأن اللي جاي أهم من اللي فات.",
    "ابقوا معنا، سنعود خلال دقائق بمعلومة قد تغيّر كل شيء.",
    "استراحة سريعة… لا تروحون بعيد.",
    "استريحوا شوي، وأنا مستني رجعتكم لكشف التفاصيل القوية.",
  ],
};

const STORE = "okfbi_cuecards";

export function getLocalPrefs() {
  try {
    const s = JSON.parse(localStorage.getItem(STORE) || "{}");
    return { pinned: s.pinned || null, cursor: s.cursor || {} };
  } catch { return { pinned: null, cursor: {} }; }
}
export function saveLocalPrefs(p) { try { localStorage.setItem(STORE, JSON.stringify(p)); } catch {} }

/** Merge default cues with host-custom lines (custom appended after defaults). */
export function mergeCues(custom) {
  const out = {};
  for (const p of PHASES) {
    const base = DEFAULT_CUES[p.key] || [];
    const extra = Array.isArray(custom?.[p.key]) ? custom[p.key] : [];
    out[p.key] = [...base, ...extra];
  }
  return out;
}

/** Generate a fresh alt-line for a phase using deterministic template swaps. */
export function generateAltCue(phaseKey) {
  const templates = ALT_TEMPLATES[phaseKey] || ["استعدوا… نكمل تفاصيل القضية بعد لحظات."];
  const idx = Math.floor(Math.random() * templates.length);
  return templates[idx];
}

const ALT_TEMPLATES = {
  opening: [
    "قضيتنا تبدأ الآن، وأنا شخصياً ما زلت في مرحلة القراءة الأولى.",
    "ليلة ثقيلة، تفاصيل غامضة، وجمهور محترف… كل شي جاهز.",
    "إذا كنتم مستعدين لملاحظة الأشياء الصغيرة، فأنتم في المكان الصحيح.",
  ],
  crime_scene: [
    "قبل ما نتحرك، خذوا لقطة كاملة للمكان في أذهانكم.",
    "لاحظوا الإضاءة والزوايا؛ الجاني ما يختار بعشوائية.",
    "المكان يحكي أكثر من الشهود أحياناً. نستمع.",
  ],
  evidence: [
    "نضيف قطعة جديدة، ونعيد قراءة كل اللي قبلها.",
    "الأدلة عندي دايم تُقسّم إلى ثلاثة: قوي، مضلّل، وشاهد صامت.",
    "لا يبني أحد استنتاجه على دليل واحد… خصوصاً هذا.",
  ],
  cctv: [
    "التسجيل بعينه ما يكفي؛ الوقت اللي جنبه أهم.",
    "خذوا بالكم من اللقطات اللي فيها فراغ… الفراغ نفسه دليل.",
    "الكاميرا ما تكذب، لكنها ما تخبر بكل شيء.",
  ],
  interrogation: [
    "في هذي الغرفة، كل ثانية تكشف حرف من الحقيقة.",
    "لاحظوا الأسئلة التي تُجاب بسرعة كبيرة… عادة تكون معدّة مسبقاً.",
    "أهدأ الشهود أحياناً هم أخطرهم.",
  ],
  fingerprint: [
    "بصمة واحدة تكفي لتغيير مسار كل شي.",
    "المختبر ما ينظر لأحد… يعطي نتيجة.",
    "قبل ما أقرأ النتيجة… ذكّروني وين كان توقعكم من البداية.",
  ],
  dna: [
    "الحمض النووي يقلّل الاحتمالات إلى شخص واحد. وين هو؟",
    "خطوة أخيرة للتأكد… لأن الخطأ في هذه المرحلة ثمنه غالي.",
    "لحظة التحقق من المختبر… ركّزوا معي.",
  ],
  voting: [
    "الفرصة الأخيرة قبل ما تُغلق أبواب التصويت.",
    "الآن نشوف مين بيثبت على قراره ومين بيغيّر رأيه.",
    "التصويت مو معركة… هو حكم.",
  ],
  accusation: [
    "خطوة واحدة تفصلنا عن نهاية القضية.",
    "أعلنها الآن… أو أعلنها بعد ثواني، القرار قرارك.",
    "لحظة الاتهام هي اللحظة اللي تختبر التحقيق كله.",
  ],
  solved: [
    "أنهينا القضية… لكن الأثر يبقى.",
    "شكراً على تحقيق أنيق. تستحقون هذا الفوز.",
    "قضيتنا القادمة ستكون أشد. جهزوا الأعصاب.",
  ],
  failed: [
    "أحياناً الحقيقة تختفي لأنها ما وجدت من يبحث بعمق كافٍ.",
    "قضية ما تُحل، لكن ما تُنسى.",
    "نتقدم ونتعلم. لا يوجد فشل في الاجتهاد.",
  ],
  intermission: [
    "لا تبتعدون، بس دقايق ونرجع.",
    "استراحة قصيرة… واللي جاي يستحق الانتظار.",
    "خذوا كوب قهوة… وارجعوا حاضرين للتفكير.",
  ],
};
